import React from "react";
import { StringSearchInput } from "./StringSearchInput";
import { NumberSearchInput } from "./NumberSearchInput";
import { DateRangeInput } from "./DateRangeInput";
import { SelectSearchInput, SelectOption } from "./SelectSearchInput";

export type SearchType = "string" | "number" | "date" | "select";

export interface SearchableColumn {
  field: string;
  headerName: string;
  searchable?: boolean;
  searchType?: SearchType;
  selectOptions?: SelectOption[]; // Options for select type
}

interface SearchInputFactoryProps {
  column: SearchableColumn;
  value: any;
  onChange: (value: any) => void;
}

export const SearchInputFactory: React.FC<SearchInputFactoryProps> = ({
  column,
  value,
  onChange,
}) => {
  const { field } = column;

  // Determine the search type based on column configuration
  const getSearchType = (): SearchType => {
    if (column.searchType) {
      return column.searchType;
    }

    // Auto-detect based on field name patterns
    const fieldLower = field.toLowerCase();

    if (
      fieldLower.includes("date") ||
      fieldLower.includes("time") ||
      fieldLower.includes("created") ||
      fieldLower.includes("updated")
    ) {
      return "date";
    }

    if (
      fieldLower.includes("id") ||
      fieldLower.includes("year") ||
      fieldLower.includes("count") ||
      fieldLower.includes("number")
    ) {
      return "number";
    }

    return "string";
  };

  const searchType = getSearchType();

  const renderSearchInput = () => {
    switch (searchType) {
      case "string":
        return (
          <StringSearchInput
            field={field}
            value={value as string | null}
            onChange={onChange}
            placeholder={column.headerName}
          />
        );

      case "number":
        return (
          <NumberSearchInput
            field={field}
            value={value as number | null}
            onChange={onChange}
            placeholder={column.headerName}
          />
        );

      case "date":
        return (
          <DateRangeInput
            field={field}
            value={value as string | null}
            onChange={onChange}
            placeholder={column.headerName}
          />
        );

      case "select":
        if (!column.selectOptions) {
          console.warn(
            `SelectSearchInput requires selectOptions for field: ${field}`
          );
          return null;
        }
        return (
          <SelectSearchInput
            field={field}
            value={value}
            onChange={onChange}
            placeholder={column.headerName}
            options={column.selectOptions}
          />
        );

      default:
        return (
          <StringSearchInput
            field={field}
            value={value as string | null}
            onChange={onChange}
            placeholder={column.headerName}
          />
        );
    }
  };

  return renderSearchInput();
};

// Helper function to create searchable column configuration
export const createSearchableColumn = (
  field: string,
  headerName: string,
  searchType?: SearchType
): SearchableColumn => ({
  field,
  headerName,
  searchable: true,
  searchType,
});

// Helper function to create non-searchable column
export const createColumn = (
  field: string,
  headerName: string
): SearchableColumn => ({
  field,
  headerName,
  searchable: false,
});
